import { defineComponent } from "vue";
import { mapState } from "pinia";
import { useVuelidate } from "@vuelidate/core";
import { helpers, required, requiredIf } from "@vuelidate/validators";
import { Container } from "aurelia-dependency-injection";
import moment from "moment";

import {
    AgencyRoles,
    ApplicationEnum,
    InternalReferralSource,
    LineOfServices,
    SourceOfReferral
} from "../../../../../../enums/enums";
import { useHospiceConvertToPending } from "../../../store/useHospiceConvertToPending";
import { formatDateInput } from "../../../../../../common/vue-helpers/modifiers/value-modifier";
import { IGetVendorSlim } from "../../../../../../resources-vue/vue-interfaces/i-vendor";
import { ITypeaheadOptions } from "../../../../../../resources-vue/vue-interfaces/i-typeahead";
import { VendorsService } from "../../../../../../services/vendor-service";
import { IGetUsersByRoleQuery, IUserResult } from "../../../../../../resources-vue/vue-interfaces/i-user";
import { formatName } from "../../../../../../common/format-name";
import { UserService } from "../../../../../../services/user-service";
import config from "../../../../../../common/config";
import { PhysiciansService } from "../../../../../../services/physician-service";
import { EnumsService } from "../../../../../../services/enums.service";
import { EnumMap } from "../../../../../../common/enum-map";
import { PendingReasons } from "../../../../../../enums/HospicePendingReasonEnum";
import { getHospiceRedirectionUrl } from "../../../../../../common/get-hospice-redirection-url";

import FormSection from "../../../../../../resources-vue/vue-custom-elements/FormSection/FormSection.vue";
import DatePicker from "../../../../../../resources-vue/vue-custom-elements/DatePicker/DatePicker.vue";
import TypeaheadInput from "../../../../../../resources-vue/vue-custom-elements/TypeaheadInput/TypeaheadInput.vue";
import CommentInput from "../../../../../../resources-vue/vue-custom-elements/CommentInput/CommentInput.vue";

export default defineComponent({
    inject: ["v"],
    components: {
        FormSection,
        DatePicker,
        TypeaheadInput,
        CommentInput
    },
    data(vm) {
        return {
            _enumService: null,
            _vendorsService: null,
            _usersService: null,
            _physicianService: null,
            lineOfServices: Array.from(LineOfServices.enumWithDescriptions.values()),
            _hospiceReferralSourceOrder: ["Physician", "Facility", "Internal", "Other"],
            referralSources: [],
            hospiceApplicationId: ApplicationEnum.AxxessHospiceFE,
            physician: null,
            facility: null,
            communityLiaison: null,
            internalReferralSource: null,
            roles: null,
            AgencyRoles: Array.from(AgencyRoles.enumWithDescriptions.values()),
            InternalReferralSource: Array.from(InternalReferralSource.enumWithDescriptions.values()),
            relationships: [],
            _relationshipsEnumMap: null,
            showRelationshipDescription: false,
            ignorePhysicianChange: false,
            ignoreFacilityChange: false,
            datePickerOptions: {} as DatepickerOptions,
            pendingReasonsOptions: Array.from(PendingReasons.enumWithDescriptions.values()),
            v$: useVuelidate(),
            formatDateInput
        };
    },
    validations() {
            const referralDateNotBeforeInquiryDate = helpers.withMessage(
                "The referral date must be on or after the inquiry date.",
                (value: string | null) => {
                    // if referral date is empty, it's invalid here
                    if (!value) return false;
    
                    // if inquiry date is not provided, allow ANY referral date
                    if (!this.patientDetails.inquiryDate) return true;
    
                    // compare dates
                    return new Date(value) >= new Date(this.patientDetails.inquiryDate);
                }
            );
            return {
                patientDetails: {
                        referralDate: {
                            required: helpers.withMessage("Referral Date is required.", required),
                            referralDateNotBeforeInquiryDate
                        },
                        referralReason: { required }
                }
            };
        },
    computed: {
        ...mapState(useHospiceConvertToPending, ["application", "patientDetails", "accountId"]),
        lineOfServiceOptions() {
            return this.lineOfServices?.map((option: any) => ({
                name: option.name,
                value: option.value
            }));
        }
    },
    async created() {
        this.roles = this.AgencyRoles;
        this._vendorsService = Container.instance.get(VendorsService);
        this._usersService = Container.instance.get(UserService);
        this._physicianService = Container.instance.get(PhysiciansService);
        this._enumService = Container.instance.get(EnumsService);
        this.referralSources = this._hospiceReferralSourceOrder.map((referralSource) => {
            return SourceOfReferral.enumWithDescriptions.get(referralSource);
        });
        await this.initOptions();
        if (!!this.patientDetails) {
            this.initValuesFromModel();
        }

        if (!!this.patientDetails?.prevDischargeDate) {
            this.datePickerOptions.startDate = moment(this.patientDetails?.prevDischargeDate).format("MM/DD/YYYY");
        }
        if (!!this.patientDetails?.currentAdmissionDate) {
            this.datePickerOptions.endDate = moment(this.patientDetails?.currentAdmissionDate).format("MM/DD/YYYY");
        }
    },

    watch: {
        async facility() {
            if (!this.facility || this.ignoreFacilityChange) {
                this.ignoreFacilityChange = false;
                return;
            }
            this.patientDetails.facilityReferralSourceId = this.facility.value;
            this.patientDetails.facilityReferralSourceName = this.facility.name;
            if (!this.facility.value) {
                return;
            }
            const facilityPayload = {
                id: this.facility.value,
                accountId: this.accountId,
                application: this.application
            };
            const selectedFacility = await this._vendorsService.getVendorById(facilityPayload);
            this.communityLiaison = {
                name: selectedFacility.communityLiaisonName,
                value: selectedFacility.communityLiaisonId
            };
        },
        physician() {
            if (!this.physician || this.ignorePhysicianChange) {
                this.ignorePhysicianChange = false;
                return;
            }
            let npiIndex = this.physician?.name?.indexOf(" - NPI");
            if (npiIndex > 0 && this.physician?.name) {
                const name = this.physician.name?.slice(0, npiIndex + 1);
                this.patientDetails.physicianReferralSourceName = name;
                this.patientDetails.physicianReferralSourceId = this.physician.value;
            } else {
                this.patientDetails.physicianReferralSourceName = this.physician.name;
                this.patientDetails.physicianReferralSourceId = this.physician.value;
            }
            this.autofillCommunityLiaison(this.physician.value);
        },

        communityLiaison() {
            if (this.communityLiaison) {
                this.patientDetails.communityLiaisonId = this.communityLiaison.value as string;
                this.patientDetails.communityLiaisonName = this.communityLiaison.name as string;
            } else {
                this.patientDetails.communityLiaisonId = null;
                this.patientDetails.communityLiaisonName = null;
            }
        },
        internalReferralSource() {
            if (this.internalReferralSource) {
                this.patientDetails.internalReferralSourceId = this.internalReferralSource.value;
                this.patientDetails.internalReferralSourceName = this.internalReferralSource.name;
            }
        }
    },

    methods: {
        async initOptions() {
            this.relationships = await this._enumService.getReferralSourceRelationship();
            this._relationshipsEnumMap = new EnumMap(this.relationships);
        },
        initValuesFromModel() {
            if (!!this.patientDetails) {
                this.handleRelationshipSelection();
                if (!this.patientDetails.sourceOfReferral && this.referralSources.length > 0) {
                    this.patientDetails.sourceOfReferral = this.referralSources[0].value as number;
                }
                if (this.patientDetails.physicianReferralSourceId && this.patientDetails.physicianReferralSourceName) {
                    this.ignorePhysicianChange = true;
                    this.physician = {
                        name: this.patientDetails.physicianReferralSourceName,
                        value: this.patientDetails.physicianReferralSourceId
                    };
                }
                if (this.patientDetails.facilityReferralSourceId && this.patientDetails.facilityReferralSourceName) {
                    this.ignoreFacilityChange = true;
                    this.facility = {
                        name: this.patientDetails.facilityReferralSourceName,
                        value: this.patientDetails.facilityReferralSourceId
                    };
                }
                if (this.patientDetails.internalReferralSourceId && this.patientDetails.internalReferralSourceName) {
                    this.internalReferralSource = {
                        name: this.patientDetails.internalReferralSourceName,
                        value: this.patientDetails.internalReferralSourceId
                    };
                }
                if (this.patientDetails.communityLiaisonId && this.patientDetails.communityLiaisonName) {
                    this.communityLiaison = {
                        name: this.patientDetails.communityLiaisonName,
                        value: this.patientDetails.communityLiaisonId
                    };
                }
            }
        },
        async autofillCommunityLiaison(physicianId: string) {
            if (!physicianId) {
                return;
            }
            const payload = {
                physicianId,
                application: this.application,
                accountId: this.accountId
            };
            const physician = await this._physicianService.getPhysicianById(payload);
            this.communityLiaison = {
                name: physician?.communityLiaisonName,
                value: physician?.communityLiaisonId
            };
        },
        handleDateChange(date: any, name: string) {
            if (name === "referralDate") {
                this.patientDetails.referralDate = date;
            }
        },
        sourceOfReferralUpdated() {
            // Facility Referral Source
            this.facility = {
                name: null,
                value: null
            };
            this.patientDetails.facilityReferralSourceContactName = null;
            this.communityLiaison = {
                name: null,
                value: null
            };
            // Physician Referral Source
            this.physician = {
                name: null,
                value: null
            };
            this.patientDetails.physicianReferralSourceContactName = null;
            this.patientDetails.sameAsAttendingPhysician = false;
            // Other Referral Source
            this.patientDetails.otherReferralSourceName = null;
            this.patientDetails.otherReferralSourceRelationship = null;
            // Internal Referral Source
            this.internalReferralSource = {
                name: null,
                value: null
            };
        },
        async getVendorList(filter: string, limit: number) {
            const payload = {
                accountId: this.accountId,
                name: filter,
                providerIds: [this.patientDetails.providerId],
                application: this.application
            };
            const facilityList = await this._vendorsService.getVendorByTerm(payload);
            return this.facilityToTypeahead(facilityList);
        },
        facilityToTypeahead(facilities: IGetVendorSlim[]): ITypeaheadOptions[] {
            return facilities.map((facility) => ({
                name: facility.name,
                value: facility.id
            }));
        },
        updateFacility(newValue: any) {
            this.facility = newValue;
        },
        async getUsersByRole(filter: string, limit: number) {
            return this.userToTypeahead(
                await this._usersService.getUsersByRole(this.getUserQuery("Community Liaison Officer", true, filter))
            );
        },
        userToTypeahead(users: IUserResult[]): ITypeaheadOptions[] {
            return users.map((user) => ({
                name: formatName(user.firstName, user.lastName),
                value: user.id
            }));
        },

        getUserQuery(userRole: string, include: boolean, filter: string): IGetUsersByRoleQuery {
            const roleCode = this.roles.filter((role: any) => role.name === userRole)[0].value;
            return {
                role: [roleCode] as number[],
                include: include,
                term: filter,
                providerIds: [this.patientDetails.providerId],
                application: Number(this.application)
            };
        },
        updateCommunityLiaison(newValue: any) {
            this.communityLiaison = newValue;
        },
        handleFacilityRedirection() {
            window.open(
                `${getHospiceRedirectionUrl(String(this.accountId))}/create/vendor?accountId=${this.accountId}`
            );
        },

        handlePhysicianRedirection() {
            window.open(
                `${getHospiceRedirectionUrl(String(this.accountId))}/create/physician?accountId=${this.accountId}`
            );
        },

        async getPhysicianList(filter: string, limit: number) {
            const data = await this._physicianService.getPhysiciansByName({
                name: filter,
                page: 1,
                pageLength: limit,
                providerIds: [this.patientDetails.providerId],
                application: this.application,
                accountId: this.accountId
            });
            return data.items?.map((item: any) => {
                const splitName = item.name.split(" ");
                return {
                    name: formatName(splitName[0], splitName[1]) + (item.npi ? ` - NPI: ${item.npi}` : ""),
                    value: item.id
                };
            });
        },
        updatePhysician(newValue: any) {
            this.physician = newValue;
        },

        handleRelationshipSelection() {
            if (!!this.patientDetails.otherReferralSourceRelationship) {
                // update dynamically later
                const enumName = this._relationshipsEnumMap.getEnumName(
                    this.patientDetails.otherReferralSourceRelationship
                );
                this.showRelationshipDescription = this._relationshipsEnumMap.checkEnumName(
                    enumName.toString(),
                    "other"
                );
            } else {
                this.showRelationshipDescription = false;
            }
        },
        resultUpdatedAppended(value: string) {
            this.patientDetails.comments += value;
        },
        resultUpdated(value: string) {
            this.patientDetails.comments = value;
        }
    }
});
